import { Alert, Platform, Linking } from 'react-native';
import DeviceInfo from 'react-native-device-info';
import VersionCheck from 'react-native-version-check';
import { ShowToast, apiLink } from '../Common/common';
import packageJson from '../../package.json';

export interface UpdateResult {
  updateAvailable: boolean;
  isForceUpdate: boolean;
  currentVersion: string;
  latestVersion: string;
  minVersion?: string;
  storeUrl?: string;
  apkUrl?: string;
  releaseNotes?: string;
  message?: string;
}

export interface AppVersionInfo {
  version: string;
  buildNumber: string;
  displayVersion: string;
}

const UPDATE_CHECK_URL = `${apiLink}version/check`;

export const getAppVersionInfo = async (): Promise<AppVersionInfo> => {
  const fallbackVersion = (packageJson && packageJson.version) || '1.0.0';
  let version = fallbackVersion;
  let buildNumber = '1';

  if (Platform.OS !== 'web') {
    try {
      if (DeviceInfo && typeof DeviceInfo.getVersion === 'function') {
        const v = await DeviceInfo.getVersion();
        if (v) version = v;
      }
      if (DeviceInfo && typeof DeviceInfo.getBuildNumber === 'function') {
        const b = await DeviceInfo.getBuildNumber();
        if (b) buildNumber = b;
      }
    } catch (err) {
      console.warn('Could not get device version info:', err);
    }
  }

  const displayVersion =
    Platform.OS === 'web'
      ? `v${version}`
      : `v${version} (${buildNumber})`;

  return {
    version,
    buildNumber,
    displayVersion,
  };
};

export const compareVersions = (v1: string, v2: string): number => {
  if (!v1 || !v2) return 0;
  const cleanV1 = v1.replace(/[^0-9.]/g, '');
  const cleanV2 = v2.replace(/[^0-9.]/g, '');
  const parts1 = cleanV1.split('.').map(Number);
  const parts2 = cleanV2.split('.').map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const part1 = parts1[i] || 0;
    const part2 = parts2[i] || 0;

    if (part1 > part2) return 1;
    if (part1 < part2) return -1;
  }

  return 0;
};

/**
 * Main update check function invoked on app load.
 * Returns an UpdateResult detailing if an update is available or mandatory.
 */
export const checkAppUpdateStatus = async (): Promise<UpdateResult> => {
  const versionInfo = await getAppVersionInfo();
  const currentVersion = versionInfo.version;

  const defaultResult: UpdateResult = {
    updateAvailable: false,
    isForceUpdate: false,
    currentVersion,
    latestVersion: currentVersion,
  };

  // Web deployments are served directly from the server; native store updates do not apply
  if (Platform.OS === 'web') {
    return defaultResult;
  }

  try {
    // 1. Fetch from BlueHMS backend API
    const apiResult = await fetchVersionFromAPI(currentVersion, versionInfo.buildNumber);
    if (apiResult) {
      return apiResult;
    }

    // 2. Fallback to store check via react-native-version-check on native platforms (skipped in test environment)
    if (
      process.env.NODE_ENV !== 'test' &&
      (Platform.OS as string) !== 'web' &&
      VersionCheck &&
      typeof VersionCheck.needUpdate === 'function'
    ) {
      try {
        const res = await VersionCheck.needUpdate({ currentVersion });
        if (res && res.isNeeded) {
          return {
            updateAvailable: true,
            isForceUpdate: false,
            currentVersion,
            latestVersion: res.latestVersion || currentVersion,
            storeUrl: res.storeUrl,
            releaseNotes: 'New performance improvements and security fixes.',
          };
        }
      } catch (vcErr) {
        // Suppress expected store lookup failures in test/dev environments
      }
    }
  } catch (error) {
    console.warn('Error during app update check:', error);
  }

  return defaultResult;
};

const fetchVersionFromAPI = async (
  currentVersion: string,
  buildNumber: string = '1',
): Promise<UpdateResult | null> => {
  try {
    const url = `${UPDATE_CHECK_URL}?version=${encodeURIComponent(
      currentVersion,
    )}&platform=${Platform.OS}&buildNumber=${encodeURIComponent(buildNumber)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.ok) {
      const data = await response.json();
      const latestVersion = data.latestVersion || currentVersion;
      const hasUpdate =
        data.updateAvailable ??
        (compareVersions(latestVersion, currentVersion) > 0);
      const isForceUpdate = Boolean(data.forceUpdate || data.isForceUpdate);

      return {
        updateAvailable: Boolean(hasUpdate),
        isForceUpdate,
        currentVersion,
        latestVersion,
        minVersion: data.minVersion,
        storeUrl: data.storeUrl || data.updateUrl || data.apkUrl,
        apkUrl: data.apkUrl,
        releaseNotes:
          data.releaseNotes ||
          'New features, stability improvements, and bug fixes.',
        message: data.message,
      };
    }
  } catch (error) {
    // Network or API not reachable - fallback gracefully
  }
  return null;
};

export const checkAppUpdateManually = async (
  onUpdateFound?: (result: UpdateResult) => void,
): Promise<void> => {
  if (Platform.OS === 'web') {
    const versionInfo = await getAppVersionInfo();
    ShowToast(`You are on the latest web version (v${versionInfo.version})`, 'success');
    return;
  }
  ShowToast('Checking for updates...', 'info');
  try {
    const result = await checkAppUpdateStatus();
    if (result.updateAvailable) {
      if (onUpdateFound) {
        onUpdateFound(result);
      } else {
        showUpdatePrompt(result);
      }
    } else {
      ShowToast(`You are on the latest version (v${result.currentVersion})`, 'success');
    }
  } catch (error) {
    ShowToast('Unable to check for updates. Please check your connection.', 'error');
  }
};

export const handleUpdate = (customStoreUrl?: string): void => {
  if (customStoreUrl) {
    Linking.canOpenURL(customStoreUrl)
      .then(supported => {
        if (supported) {
          return Linking.openURL(customStoreUrl);
        }
        fallbackPlatformUpdate();
      })
      .catch(() => fallbackPlatformUpdate());
    return;
  }
  fallbackPlatformUpdate();
};

const fallbackPlatformUpdate = (): void => {
  if (Platform.OS === 'android') {
    const packageName = 'com.bluetech.bhms';
    const playStoreUrl = `market://details?id=${packageName}`;
    const webUrl = `https://play.google.com/store/apps/details?id=${packageName}`;

    Linking.canOpenURL(playStoreUrl)
      .then(supported => {
        if (supported) {
          return Linking.openURL(playStoreUrl);
        } else {
          return Linking.openURL(webUrl);
        }
      })
      .catch(() => {
        ShowToast('Could not open Play Store', 'error');
      });
  } else if (Platform.OS === 'ios') {
    const appId = 'your-app-id';
    const appStoreUrl = `itms-apps://apps.apple.com/app/id${appId}`;
    const webUrl = `https://apps.apple.com/app/id${appId}`;

    Linking.canOpenURL(appStoreUrl)
      .then(supported => {
        if (supported) {
          return Linking.openURL(appStoreUrl);
        } else {
          return Linking.openURL(webUrl);
        }
      })
      .catch(() => {
        ShowToast('Could not open App Store', 'error');
      });
  } else {
    ShowToast('Reloading page for latest update...', 'info');
    setTimeout(() => {
      if (typeof window !== 'undefined' && window.location) {
        window.location.reload();
      }
    }, 1000);
  }
};

// Legacy exports for backward compatibility
export const checkForAppUpdate = async (): Promise<void> => {
  const result = await checkAppUpdateStatus();
  if (result.updateAvailable) {
    showUpdatePrompt(result);
  }
};

export const checkForForceUpdate = async (): Promise<boolean> => {
  const result = await checkAppUpdateStatus();
  return result.updateAvailable && result.isForceUpdate;
};

export const showUpdatePrompt = (info: UpdateResult): void => {
  Alert.alert(
    info.isForceUpdate ? 'Critical Update Required' : 'Update Available',
    `A new version (${info.latestVersion}) of BlueHMS is available. Please update to get the latest features and improvements.`,
    info.isForceUpdate
      ? [
          {
            text: 'Update Now',
            onPress: () => handleUpdate(info.storeUrl),
          },
        ]
      : [
          {
            text: 'Later',
            style: 'cancel',
          },
          {
            text: 'Update Now',
            onPress: () => handleUpdate(info.storeUrl),
          },
        ],
    { cancelable: !info.isForceUpdate }
  );
};
